import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface PlanoContas {
  id: number;
  codigoPlanoConta?: string;
  descricaoConta?: string;
  contaMestreId?: number | null;
  tipoContaId?: number | null;      // 0=Sintética, 1=Analítica
  naturezaContaId?: number | null;  // 1=Ativo, 2=Passivo, 3=PatrimônioLíquido, 4=Resultado, 5=Compensação, 9=Outras
  tipoNotaId?: number | null;       // 0=Nenhum, 1=Entrada, 2=Saída
  centroCustoId?: number | null;
  formaPagRecPadraoId?: number | null;
  referencia?: string;
  observacao?: string;
  inativo?: number | null;          // 0=Ativo, 1=Inativo
}

export interface PlanoContasFormData {
  codigoPlanoConta?: string;
  descricaoConta?: string;
  contaMestreId?: number | null;
  tipoContaId?: number | null;
  naturezaContaId?: number | null;
  tipoNotaId?: number | null;
  centroCustoId?: number | null;
  formaPagRecPadraoId?: number | null;
  referencia?: string;
  observacao?: string;
  inativo?: number | null;
}

export function usePlanoContas() {
  const queryClient = useQueryClient();

  const { data: plano = [], isLoading } = useQuery({
    queryKey: ["plano-contas"],
    queryFn: async () => {
      const { data } = await apiClient.get<PlanoContas[]>("/api/plano-contas");
      return data;
    },
  });

  const createConta = useMutation({
    mutationFn: async (formData: PlanoContasFormData) => {
      const { data } = await apiClient.post<PlanoContas>("/api/plano-contas", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plano-contas"] });
      toast.success("Conta criada com sucesso!");
    },
    onError: (error: Error) => toast.error("Erro ao criar conta: " + error.message),
  });

  const updateConta = useMutation({
    mutationFn: async ({ id, ...formData }: PlanoContasFormData & { id: number }) => {
      const { data } = await apiClient.put<PlanoContas>(`/api/plano-contas/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plano-contas"] });
      toast.success("Conta atualizada com sucesso!");
    },
    onError: (error: Error) => toast.error("Erro ao atualizar conta: " + error.message),
  });

  const deleteConta = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/plano-contas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plano-contas"] });
      toast.success("Conta inativada com sucesso!");
    },
    onError: (error: Error) => toast.error("Erro ao inativar conta: " + error.message),
  });

  return { plano, isLoading, createConta, updateConta, deleteConta };
}
